import React from 'react';
import { Link } from 'react-router-dom';
import StickyHeader from '../components/StickyHeader';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { ArrowRight, Shield, Users, Sparkles } from 'lucide-react';
import { openWhatsApp } from '../utils/whatsappUtils';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Navigation */}
      <StickyHeader />

      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
            About ComplyKit
          </h1>
          <p className="text-xl text-slate-600 leading-relaxed">
            A modern compliance platform built for clarity, accountability, and peace of mind.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <div className="space-y-12">
            
            {/* What we are */}
            <div>
              <h2 className="text-2xl font-semibold mb-4 text-slate-900">What we do</h2>
              <p className="text-slate-600 leading-relaxed">
                ComplyKit is an AI-powered compliance platform for India. We help individuals, 
                founders, and businesses navigate regulatory requirements—from company registration 
                and GST filings to income tax returns and trademark protection.
              </p>
            </div>

            {/* Who it's for */}
            <div>
              <h2 className="text-2xl font-semibold mb-4 text-slate-900">Who we serve</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Our platform is designed for anyone dealing with compliance in India:
              </p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span>Individuals filing income tax returns or managing personal finances</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span>Founders starting their first company or expanding operations</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span>Businesses maintaining ongoing compliance across GST, MCA, and more</span>
                </li>
              </ul>
            </div>

            {/* How it works */}
            <div>
              <h2 className="text-2xl font-semibold mb-4 text-slate-900">How it works</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We combine AI automation with human expertise:
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                  <Sparkles className="h-5 w-5 text-slate-700 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-slate-900 mb-1">AI handles the routine</p>
                    <p className="text-sm text-slate-600">Document preparation, deadline tracking, form generation, and obligation mapping.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                  <Users className="h-5 w-5 text-slate-700 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-slate-900 mb-1">Experts handle the judgment calls</p>
                    <p className="text-sm text-slate-600">Qualified CAs, Company Secretaries, and legal professionals review filings, interpret regulations, and handle complex matters.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Philosophy */}
            <div>
              <h2 className="text-2xl font-semibold mb-4 text-slate-900">Our philosophy</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Compliance shouldn't be confusing, expensive, or opaque. We believe in:
              </p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">Clarity</strong> — Know exactly what's happening at every step</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">Accountability</strong> — Clear ownership of who handles what</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-slate-900">Transparent pricing</strong> — Upfront fees, no surprises, government charges billed at actuals</span>
                </li>
              </ul>
            </div>

            {/* Expert network */}
            <div>
              <h2 className="text-2xl font-semibold mb-4 text-slate-900">Our expert network</h2>
              <p className="text-slate-600 leading-relaxed">
                Every filing, registration, and compliance task on ComplyKit is reviewed by qualified 
                professionals. Our network includes practicing Chartered Accountants, Company Secretaries, 
                and legal experts who ensure accuracy and regulatory compliance. You get the efficiency of 
                automation with the reliability of human oversight.
              </p>
            </div>

            {/* Accuracy */}
            <div className="p-6 bg-slate-900 rounded-xl text-white">
              <div className="flex items-start gap-4">
                <Shield className="h-6 w-6 text-slate-300 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold mb-2">Built for accuracy</h3>
                  <p className="text-slate-300 leading-relaxed">
                    Compliance errors are costly. That's why we've designed our systems around accuracy first—
                    with multiple review checkpoints, automated validation, and expert sign-off on every submission.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="pt-8 border-t border-slate-200">
              <p className="text-slate-600 mb-4">Ready to simplify your compliance?</p>
              <Button className="gap-2" onClick={() => openWhatsApp('homepage')}>
                Get Started
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
